import React from "react";
import { store } from "../store";
import { setAIReview } from "../store/actions";
import { ToastManager } from "../components/organisms/CommonUI/ToastPopup/ToastManager";
import { AIReviewStatus, AIReview } from "../common/models/types";
import {
  fetchAnalysisStream,
  stopAnalysisStream,
  reviewStreamPool,
  AiReviewStreamer,
} from "../api/aiReviewService";
import { log, LogLevel } from "../utils/LogUtil";
import { CompletionToast, FailureToast } from "./StreamToast";

class StreamManager {
  private static instance: StreamManager;
  private activeStreams: Map<number, AiReviewStreamer> = new Map();
  private previousStatuses: Map<number, AIReviewStatus> = new Map();
  private streamTimeouts: Map<number, NodeJS.Timeout> = new Map();
  private streamQueue: number[] = [];
  private maxConcurrentStreams = 10;
  private readonly STREAM_TIMEOUT = 30000; // 30초

  static getInstance(): StreamManager {
    if (!StreamManager.instance) {
      StreamManager.instance = new StreamManager();
    }
    return StreamManager.instance;
  }

  startStream(reviewId: number): void {
    log(LogLevel.INFO, `[StreamManager] ========== START STREAM ${reviewId} ==========`);
    log(LogLevel.INFO, `[StreamManager] Current active streams: ${this.activeStreams.size}/${this.maxConcurrentStreams}`);
    log(LogLevel.INFO, `[StreamManager] Queue length: ${this.streamQueue.length}`);
    log(LogLevel.INFO, `[StreamManager] Active stream IDs: [${Array.from(this.activeStreams.keys()).join(', ')}]`);
    
    try {
      // 이미 연결된 stream이 있으면 재사용
      if (this.activeStreams.has(reviewId)) {
        log(LogLevel.WARNING, `[StreamManager] Stream already active for review ${reviewId} - skipping`);
        return;
      }

      // 동시 연결 수 제한 확인
      if (this.activeStreams.size >= this.maxConcurrentStreams) {
        log(
          LogLevel.WARNING,
          `[StreamManager] MAX STREAMS REACHED (${this.maxConcurrentStreams}). Adding review ${reviewId} to queue position ${this.streamQueue.length + 1}`
        );
        
        // 큐에 추가
        if (!this.streamQueue.includes(reviewId)) {
          this.streamQueue.push(reviewId);
          log(LogLevel.INFO, `[StreamManager] Queue after adding: [${this.streamQueue.join(', ')}]`);
          
          // 사용자에게 알림
          ToastManager.show({
            content: `AI 리뷰 #${reviewId}가 대기열에 추가되었습니다. (대기: ${this.streamQueue.length}개)`,
            duration: 3000,
            position: "top-right",
          });
        } else {
          log(LogLevel.WARNING, `[StreamManager] Review ${reviewId} already in queue`);
        }
        return;
      }

      log(LogLevel.INFO, `[StreamManager] Creating new stream for review ${reviewId}`);

      // PENDING 상태 타임아웃 설정
      this.setupStreamTimeout(reviewId);

      // 새 stream 연결
      fetchAnalysisStream(
        reviewId,
        // onStatus - Redux store 직접 업데이트
        (status: AIReviewStatus, message: string, queueData?: any) => {
          const previousStatus = this.previousStatuses.get(reviewId);
          const statusName = this.getStatusName(status);
          const previousStatusName = previousStatus ? this.getStatusName(previousStatus) : 'NONE';
          
          log(
            LogLevel.INFO,
            `[StreamManager] STATUS UPDATE for review ${reviewId}: ${previousStatusName}(${previousStatus || 0}) → ${statusName}(${status}) - "${message}"`
          );
          
          // PENDING 상태에서 대기열 정보가 있을 때 추가 로깅
          if (status === AIReviewStatus.PENDING && queueData) {
            log(LogLevel.WARNING, `[StreamManager] 🚦 PENDING(Queue) status for review ${reviewId}:`, {
              queuePosition: queueData?.queuePosition,
              totalPending: queueData?.queueInfo?.totalPending,
              estimatedTime: queueData?.estimatedTimeSeconds
            });
          }
          
          try {
            // Redux store 업데이트 (queue 정보 포함)
            const updateData: any = { id: reviewId, status };
            if (queueData) {
              updateData.queuePosition = queueData.queuePosition;
              updateData.totalQueue = queueData.queueInfo?.totalPending;
              updateData.estimatedTimeSeconds = queueData.estimatedTimeSeconds;
            }
            store.dispatch(setAIReview(updateData));

            // PENDING이 아닌 상태로 변경되면 타임아웃 해제
            if (status !== AIReviewStatus.PENDING) {
              log(LogLevel.INFO, `[StreamManager] Clearing timeout for review ${reviewId} (status: ${statusName})`);
              this.clearStreamTimeout(reviewId);
            }

            // 상태가 COMPLETED 또는 FAILED로 변경된 경우 Toast 알림
            if (
              (status === AIReviewStatus.COMPLETED || status === AIReviewStatus.FAILED) &&
              previousStatus !== status
            ) {
              log(LogLevel.INFO, `[StreamManager] Status changed to ${status}, showing toast`);
              const state = store.getState();
              const patientId = state.data.reviews[reviewId]?.patientId;
              log(LogLevel.INFO, `[StreamManager] PatientId: ${patientId}`);

              if (status === AIReviewStatus.COMPLETED) {
                log(LogLevel.INFO, `[StreamManager] Showing success toast for review ${reviewId}`);

                ToastManager.show({
                  content: React.createElement(CompletionToast, { patientId, reviewId }),
                  duration: 5000,
                  position: "top-right",
                  onClick: () => {
                    log(LogLevel.INFO, `[StreamManager] Toast clicked, opening review ${reviewId}`);
                    // 새 탭에서 열기 - 현재 작업 보존
                    window.open(`/patients/${patientId}/reviews/${reviewId}`, "_blank");
                  },
                });
              } else if (status === AIReviewStatus.FAILED) {
                log(LogLevel.INFO, `[StreamManager] Showing failure toast for review ${reviewId}`);

                ToastManager.show({
                  content: React.createElement(FailureToast, { patientId, reviewId }),
                  duration: 5000,
                  position: "top-right",
                  onClick: () => {
                    log(LogLevel.INFO, `[StreamManager] Toast clicked, opening review ${reviewId}`);
                    window.open(`/patients/${patientId}/reviews/${reviewId}`, "_blank");
                  },
                });
              }
            }

            this.previousStatuses.set(reviewId, status);
          } catch (error) {
            log(
              LogLevel.ERROR,
              `[StreamManager] Error handling status update for review ${reviewId}:`,
              error
            );
          }
        },
        // onReview - Redux store 직접 업데이트
        (review: AIReview) => {
          log(LogLevel.INFO, `[StreamManager] onReview called for review ${reviewId}`);
          try {
            store.dispatch(setAIReview({ id: reviewId, ...review }));
          } catch (error) {
            log(LogLevel.ERROR, `[StreamManager] Error updating review ${reviewId}:`, error);
          }
        },
        // onError
        (error) => {
          log(LogLevel.ERROR, `[StreamManager] Stream error for review ${reviewId}:`, error);
          this.cleanupStream(reviewId);

          // EventSource가 자동으로 재연결을 시도하므로
          // 여기서는 로깅과 정리만 수행
        },
        // onComplete
        () => {
          log(LogLevel.INFO, `[StreamManager] Stream completed for review ${reviewId}`);
          this.cleanupStream(reviewId);
        }
      );

      // stream 인스턴스 저장
      const streamer = reviewStreamPool.get(reviewId);
      if (streamer) {
        this.activeStreams.set(reviewId, streamer);
        log(LogLevel.INFO, `[StreamManager] Stream started successfully for review ${reviewId}`);
      } else {
        log(
          LogLevel.WARNING,
          `[StreamManager] Failed to get streamer instance for review ${reviewId}`
        );
      }
    } catch (error) {
      log(LogLevel.ERROR, `Failed to start stream for review ${reviewId}:`, error);
      // 초기 연결 실패 시 사용자에게 알림
      ToastManager.show({
        content: `보고서 #${reviewId} 실시간 업데이트 연결에 실패했습니다.`,
        duration: 5000,
        position: "top-right",
      });
    }
  }

  private getStatusName(status: AIReviewStatus): string {
    const statusMap: { [key: number]: string } = {
      [-1]: 'FAILED',
      [0]: 'NONE',
      [100]: 'PENDING',
      [200]: 'READY',
      [300]: 'PROCESSING',
      [310]: 'PROCESSING_USER_INFO',
      [320]: 'PROCESSING_PRESCRIPTION',
      [330]: 'PROCESSING_DRUG_CRAWLING',
      [340]: 'PROCESSING_DRUG_INFO',
      [350]: 'PROCESSING_PROMPT_CREATION',
      [360]: 'PROCESSING_LLM_ANALYSIS',
      [400]: 'COMPLETED'
    };
    return statusMap[status] || `UNKNOWN(${status})`;
  }

  private setupStreamTimeout(reviewId: number): void {
    // 기존 타임아웃이 있으면 먼저 제거
    this.clearStreamTimeout(reviewId);
    
    log(LogLevel.INFO, `[StreamManager] Setting up ${this.STREAM_TIMEOUT/1000}s timeout for review ${reviewId}`);
    
    const timeout = setTimeout(() => {
      const review = store.getState().data.reviews[reviewId];
      const statusName = review?.status ? this.getStatusName(review.status) : 'UNKNOWN';
      
      log(LogLevel.WARNING, `[StreamManager] TIMEOUT CHECK for review ${reviewId}: current status = ${statusName}(${review?.status})`);
      
      if (review?.status === AIReviewStatus.PENDING) {
        log(LogLevel.ERROR, `[StreamManager] ⚠️ TIMEOUT TRIGGERED: Review #${reviewId} stuck in PENDING for ${this.STREAM_TIMEOUT / 1000}s`);
        
        // 사용자에게 알림
        ToastManager.show({
          content: `AI 리뷰 #${reviewId}가 대기 상태에서 응답이 없습니다. 재시작합니다.`,
          duration: 5000,
          position: "top-right",
        });
        
        // 스트림 재시작
        this.restartStream(reviewId);
      } else {
        log(LogLevel.INFO, `[StreamManager] Timeout check passed - review ${reviewId} is in ${statusName} status`);
      }
    }, this.STREAM_TIMEOUT);
    
    this.streamTimeouts.set(reviewId, timeout);
  }

  private clearStreamTimeout(reviewId: number): void {
    const timeout = this.streamTimeouts.get(reviewId);
    if (timeout) {
      clearTimeout(timeout);
      this.streamTimeouts.delete(reviewId);
    }
  }

  private processQueue(): void {
    if (this.streamQueue.length > 0 && this.activeStreams.size < this.maxConcurrentStreams) {
      const nextReviewId = this.streamQueue.shift();
      if (nextReviewId) {
        log(LogLevel.INFO, `[StreamManager] Processing queued stream for review ${nextReviewId}`);
        this.startStream(nextReviewId);
      }
    }
  }

  private cleanupStream(reviewId: number): void {
    log(LogLevel.INFO, `[StreamManager] Cleaning up stream for review ${reviewId}`);
    
    const wasActive = this.activeStreams.has(reviewId);
    this.activeStreams.delete(reviewId);
    this.previousStatuses.delete(reviewId);
    this.clearStreamTimeout(reviewId);
    
    // 큐에서도 제거
    const queueIndex = this.streamQueue.indexOf(reviewId);
    if (queueIndex > -1) {
      this.streamQueue.splice(queueIndex, 1);
      log(LogLevel.INFO, `[StreamManager] Removed review ${reviewId} from queue at position ${queueIndex + 1}`);
    }
    
    log(LogLevel.INFO, `[StreamManager] After cleanup - Active: ${this.activeStreams.size}, Queue: ${this.streamQueue.length}`);
    
    // 큐 처리 (활성 스트림이었던 경우에만)
    if (wasActive) {
      this.processQueue();
    }
  }

  stopStream(reviewId: number): void {
    try {
      log(LogLevel.INFO, `Stopping stream for review ${reviewId}`);
      stopAnalysisStream(reviewId);
      this.cleanupStream(reviewId);
    } catch (error) {
      log(LogLevel.ERROR, `Error stopping stream for review ${reviewId}:`, error);
    }
  }

  restartStream(reviewId: number): void {
    log(LogLevel.INFO, `[StreamManager] Restarting stream for review ${reviewId}`);
    
    // 기존 스트림이 있으면 먼저 종료
    if (this.activeStreams.has(reviewId)) {
      log(LogLevel.INFO, `[StreamManager] Stopping existing stream for review ${reviewId}`);
      this.stopStream(reviewId);
    }
    
    // 잠시 대기 후 새 스트림 시작 (EventSource 정리를 위해)
    setTimeout(() => {
      log(LogLevel.INFO, `[StreamManager] Starting new stream for review ${reviewId}`);
      this.startStream(reviewId);
    }, 100);
  }

  stopAllStreams(): void {
    log(LogLevel.INFO, `Stopping all ${this.activeStreams.size} active streams`);
    this.activeStreams.forEach((_, reviewId) => {
      this.stopStream(reviewId);
    });
  }

  // 메모리 누수 방지를 위한 cleanup 메서드 (로그아웃 시 호출)
  cleanup(): void {
    this.stopAllStreams();
    this.activeStreams.clear();
    this.previousStatuses.clear();
  }
}

export { StreamManager };
